import {
  InvoiceStatus,
  IPayeeEngagementResponse
} from "@wingspanhq/payments/dist/interfaces";
import { EngagementStatus } from "@wingspanhq/wingspan-api-sdk";
import { WSActionsMenuItem } from "@wingspanhq/fe-component-library/dist/lib/components/WSActions/WSActions";
import { paymentsService } from "../../../services/payments";
import { PayerPayeeEngagementStatus } from "@wingspanhq/payments/dist/interfaces/payerPayeeEngagement";
import { useArchiveEngagementModal } from "../modals/useArchivalEngagementModal";
import { useDeleteEngagementModal } from "../modals/useDeletionEngagementModal";
import { useArchivalNotAllowedModal } from "../modals/useArchivalNotAllowedModal";
import { useDeletionNotAllowedModal } from "../modals/useDeletionNotAllowedModal";
import { useRestoreEngagementModal } from "../modals/useRestorationEngagementModal";
import { useRestorationNotAllowedModal } from "../modals/useRestorationNotAllowedModal";
import { queriesEngagements, queriesWorkLogs } from "@query";
import { useXWingspanPayerId } from "../../Payees/routes/RoutePayeeDetails/useXWingspanPayerId";

type Props = {
  payeeId: string;
  payeeName: string;
  engagement: IPayeeEngagementResponse;
};

export const useEngagementDefaultActions = () => {
  const archiveEngagementModal = useArchiveEngagementModal();
  const deleteEngagementModal = useDeleteEngagementModal();
  const archivalNotAllowedModal = useArchivalNotAllowedModal();
  const deletionNotAllowedModal = useDeletionNotAllowedModal();
  const restoreEngagementModal = useRestoreEngagementModal();
  const restorationNotAllowedModal = useRestorationNotAllowedModal();

  const xWingspanPayerId = useXWingspanPayerId();

  return async (props: Props) => {
    const actions: WSActionsMenuItem[] = [];

    const { summary: activePayablesSummary } =
      await paymentsService.payable.list({
        filter: {
          memberClientId: props.engagement.payerPayeeEngagementId,
          status: {
            in: [
              InvoiceStatus.Pending,
              InvoiceStatus.Open,
              InvoiceStatus.Overdue
            ]
          }
        },
        page: {
          size: 1
        }
      });

    const { summary: relatedPayablesSummary } =
      await paymentsService.payable.list({
        filter: {
          memberClientId: props.engagement.payerPayeeEngagementId
        },
        page: {
          size: 1
        }
      });

    const engagementType = props.engagement.engagementId
      ? await queriesEngagements.getEngagement.fetchQueryData({
          engagementId: props.engagement.engagementId,
          xWingspanAccount: xWingspanPayerId
        })
      : undefined;

    // TODO(v3): work-log fan-out for the delete-guard now reads v3 listWorkLogs by
    // `payeeEngagementId` (v1 `payerPayeeEngagementId`). v1↔v3 relationship id-space
    // alignment is unverified (runtime risk).
    const workLogsList = await queriesWorkLogs.listWorkLogs.fetchQueryData({
      filter: {
        payeeEngagementId: { eq: props.engagement.payerPayeeEngagementId }
      }
    });
    const workLogs = workLogsList.data ?? [];

    const hasActivePayables = activePayablesSummary.listSize > 0;
    const hasAnyPayables = relatedPayablesSummary.listSize > 0;
    const engagementTypeIsActive =
      engagementType?.status === EngagementStatus.ACTIVE;
    const hasActiveLogs = workLogs.length > 0;

    if (props.engagement.status === PayerPayeeEngagementStatus.Active) {
      if (hasActivePayables) {
        actions.push({
          disabled: true,
          onDisabledClick: async () => {
            await archivalNotAllowedModal.open({
              payeeId: props.payeeId,
              payeeName: props.payeeName,
              engagement: props.engagement
            });
          },
          label: "Archive",
          icon: "archive"
        });
      } else {
        actions.push({
          onClick: async () => {
            await archiveEngagementModal.open({
              payeeId: props.payeeId,
              payeeName: props.payeeName,
              engagement: props.engagement
            });
          },
          label: "Archive",
          icon: "archive"
        });
      }
    } else {
      if (engagementTypeIsActive || !props.engagement.engagementId) {
        actions.push({
          onClick: async () => {
            await restoreEngagementModal.open({
              payeeId: props.payeeId,
              payeeName: props.payeeName,
              engagement: props.engagement
            });
          },
          label: "Restore",
          icon: "redo"
        });
      } else {
        actions.push({
          disabled: true,
          onDisabledClick: async () => {
            await restorationNotAllowedModal.open({
              payeeId: props.payeeId,
              payeeName: props.payeeName,
              engagement: props.engagement
            });
          },
          label: "Restore",
          icon: "redo"
        });
      }
    }

    if (hasAnyPayables || hasActiveLogs) {
      actions.push({
        disabled: true,
        onDisabledClick: async () => {
          await deletionNotAllowedModal.open({
            payeeId: props.payeeId,
            payeeName: props.payeeName,
            engagement: props.engagement
          });
        },
        label: "Delete",
        icon: "trash"
      });
    } else {
      actions.push({
        onClick: async () => {
          await deleteEngagementModal.open({
            payeeId: props.payeeId,
            payeeName: props.payeeName,
            engagement: props.engagement
          });
        },
        label: "Delete",
        icon: "trash",
        destructive: true
      });
    }

    return actions;
  };
};
